"use client";

import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { HolidayEffectType } from "@plexstore/types";

import { cn } from "@/lib/utils";

const PARTICLES: Record<HolidayEffectType, { glyphs: string[]; color: string; label: string }> = {
  christmas: { glyphs: ["❄", "❅", "❆"], color: "#e0f2ff", label: "Snow" },
  halloween: { glyphs: ["🦇", "👻", "🎃", "🕷"], color: "#f97316", label: "Spooky" },
  valentines: { glyphs: ["❤", "💕", "💗"], color: "#f472b6", label: "Hearts" },
  "new-year": { glyphs: ["✨", "🎉", "🎊", "★"], color: "#fde047", label: "Fireworks" },
  easter: { glyphs: ["🥚", "🐰", "🌸", "🌷"], color: "#a7f3d0", label: "Easter" },
  "black-friday": { glyphs: ["🏷", "$", "%", "💸"], color: "#fbbf24", label: "Deals" },
};

interface HolidayEffectsProps {
  enabled: boolean;
  type: HolidayEffectType | null;
}

function seeded(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
}

/** Full-viewport falling particles with a toggle FAB (state persisted in sessionStorage like the legacy UI). */
export function HolidayEffects({ enabled, type }: HolidayEffectsProps) {
  const [active, setActive] = useState(enabled);
  useEffect(() => {
    const stored = window.sessionStorage.getItem("holidayEffectsEnabled");
    if (stored !== null) setActive(stored === "true");
  }, []);

  const toggle = () => {
    const next = !active;
    setActive(next);
    window.sessionStorage.setItem("holidayEffectsEnabled", String(next));
  };

  const particles = useMemo(() => {
    if (!type) return [];
    const rand = seeded(42);
    const set = PARTICLES[type];
    return Array.from({ length: 36 }, (_, i) => ({
      key: i,
      glyph: set.glyphs[Math.floor(rand() * set.glyphs.length)] ?? set.glyphs[0]!,
      left: `${(rand() * 100).toFixed(1)}%`,
      delay: `${(-rand() * 14).toFixed(1)}s`,
      duration: `${(9 + rand() * 9).toFixed(1)}s`,
      size: `${(0.8 + rand() * 1.2).toFixed(2)}rem`,
      drift: `${((rand() - 0.5) * 120).toFixed(0)}px`,
    }));
  }, [type]);

  if (!enabled || !type) return null;
  const set = PARTICLES[type];

  return (
    <>
      {active && (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-[60] overflow-hidden">
          {particles.map((p) => (
            <span
              key={p.key}
              className="holiday-particle absolute -top-10 select-none"
              style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration, fontSize: p.size, color: set.color, "--drift": p.drift } as React.CSSProperties}
            >
              {p.glyph}
            </span>
          ))}
          <style>{`
            .holiday-particle { animation-name: holiday-fall; animation-timing-function: linear; animation-iteration-count: infinite; opacity: .85; text-shadow: 0 0 6px rgba(0,0,0,.4); }
            @keyframes holiday-fall { 0% { transform: translate3d(0,0,0) rotate(0deg); } 100% { transform: translate3d(var(--drift), 110vh, 0) rotate(360deg); } }
          `}</style>
        </div>
      )}
      <button
        type="button"
        onClick={toggle}
        aria-pressed={active}
        title={`${active ? "Disable" : "Enable"} ${set.label} effects`}
        className={cn(
          "fixed bottom-6 right-6 z-[61] flex size-14 items-center justify-center rounded-full border border-white/10 text-white shadow-lg transition hover:-translate-y-0.5",
          active ? "bg-brand accent-glow" : "glass",
        )}
      >
        <Sparkles className="size-5" />
      </button>
    </>
  );
}
